import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X, LogOut, ArrowRight, Award, BookOpen, FileText, Users, Star, Globe, Mail, CheckCircle, Shield, Target } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AdminNav from '../components/AdminNav';
import { journalFeatureService, authService } from '../services/api';

const iconMap = {
  BookOpen,
  Users,
  FileText,
  Award,
  Star,
  Globe,
  Mail,
  CheckCircle,
  Shield,
  Target,
};

const iconOptions = Object.keys(iconMap);

function AdminJournalFeatures() {
  const navigate = useNavigate();
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ title: '', description: '', icon: 'BookOpen', displayOrder: 0 });

  useEffect(() => {
    const fetchFeatures = async () => {
      try {
        const response = await journalFeatureService.getAll();
        if (response.data) {
          setFeatures(response.data);
        }
      } catch (err) {
        setError('خطا در دریافت ویژگی‌ها');
      } finally {
        setLoading(false);
      }
    };
    fetchFeatures();
  }, []);

  const handleLogout = () => {
    authService.logout();
    navigate('/admin-login');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, displayOrder: parseInt(formData.displayOrder) || 0 };
      let updated;
      if (editingId) {
        const response = await journalFeatureService.update(editingId, data);
        updated = features.map(f => f.id === editingId ? response.data : f);
      } else {
        const response = await journalFeatureService.create(data);
        updated = [...features, response.data];
      }
      setFeatures(updated);
      setShowForm(false);
      setEditingId(null);
      setFormData({ title: '', description: '', icon: 'BookOpen', displayOrder: 0 });
      setError('');
    } catch (err) {
      setError('خطا در ذخیره ویژگی');
    }
  };

  const handleEdit = (feature) => {
    setFormData({
      title: feature.title,
      description: feature.description,
      icon: feature.icon,
      displayOrder: feature.displayOrder,
    });
    setEditingId(feature.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا مطمئن هستید؟')) return;
    try {
      await journalFeatureService.delete(id);
      setFeatures(features.filter(f => f.id !== id));
    } catch (err) {
      setError('خطا در حذف ویژگی');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ title: '', description: '', icon: 'BookOpen', displayOrder: 0 });
  };

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center h-64">
        <div className="text-gray-500">در حال بارگذاری...</div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold mb-2">مدیریت ویژگی‌های نشریه</h1>
              <p className="text-xl text-blue-100">ویرایش ویژگی‌های نمایش داده شده در صفحه اصلی</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => navigate('/admin/articles')} className="flex items-center gap-2 px-4 py-2 bg-blue-700 hover:bg-blue-600 rounded-lg font-semibold transition">
                <ArrowRight size={18} />
                بازگشت
              </button>
              <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition">
                <LogOut size={18} />
                خروج
              </button>
            </div>
          </div>
        </div>
      </div>

      <AdminNav />

      <div className="max-w-7xl mx-auto px-4 py-12">
        {error && <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">{error}</div>}

        {!showForm && (
          <button onClick={() => setShowForm(true)} className="mb-8 flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-bold hover:from-green-700 hover:to-green-800 transition shadow-lg">
            <Plus size={20} />
            افزودن ویژگی
          </button>
        )}

        {showForm && (
          <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-md p-8 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">{editingId ? 'ویرایش ویژگی' : 'افزودن ویژگی جدید'}</h2>
              <button type="button" onClick={handleCancel} className="p-2 hover:bg-gray-200 rounded-lg transition"><X size={24} className="text-gray-600" /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">عنوان</label>
                <input type="text" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} required className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">ترتیب نمایش</label>
                <input type="number" value={formData.displayOrder} onChange={e => setFormData({ ...formData, displayOrder: e.target.value })} required className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">آیکون</label>
                <select value={formData.icon} onChange={e => setFormData({ ...formData, icon: e.target.value })} className="w-full px-4 py-3 border border-gray-300 rounded-lg">
                  {iconOptions.map(icon => (
                    <option key={icon} value={icon}>{icon}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">توضیحات</label>
                <input type="text" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} required className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
              </div>
            </div>
            <div className="flex gap-4 mt-6">
              <button type="submit" className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition">ذخیره</button>
              <button type="button" onClick={handleCancel} className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-400 transition">انصراف</button>
            </div>
          </form>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.length ? features.map(feature => {
            const Icon = iconMap[feature.icon] || BookOpen;
            return (
              <div key={feature.id} className="bg-white rounded-xl shadow-md p-6 border border-blue-100">
                <div className="w-14 h-14 bg-blue-700 rounded-lg flex items-center justify-center mb-4">
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 mb-4 text-sm">{feature.description}</p>
                <p className="text-xs text-gray-400 mb-4">ترتیب: {feature.displayOrder}</p>
                <div className="flex gap-2">
                  <button onClick={() => handleEdit(feature)} className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"><Edit2 size={16} /></button>
                  <button onClick={() => handleDelete(feature.id)} className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"><Trash2 size={16} /></button>
                </div>
              </div>
            );
          }) : (
            <div className="col-span-full bg-white rounded-xl shadow-md p-8 text-center text-gray-500">
              <p>هیچ ویژگی‌ای برای نمایش وجود ندارد</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminJournalFeatures;
